import { SyntheticEvent, useMemo } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { useShell } from '@/providers';
import { usePrivate } from '@/components/private';
import { useRegistryRoutes } from '@/hooks/useRegistryRoutes';
import { UserRole } from '@/lib/types';
import {
  AdjustmentsVerticalIcon,
  AnimalsIcon,
  GroupIcon,
  LeadingIcon,
} from '@/components/icons';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { navigationMessages } from './messages';
import ShellNavItems from './ShellNavItems';
import { Box } from '@/primitives';

export default function ShellSidebarLinks() {
  const pathname = usePathname();
  const { orgName } = useParams<{ orgName: string }>();
  const { userOrganisations } = usePrivate();
  const registryRoutes = useRegistryRoutes();
  const { configMenuOpen, sidebarExpanded, setConfigMenuOpen } = useShell();

  const canManageUserAssignments = useMemo(() => {
    const org = userOrganisations?.find(
      (o) => o.organisation?.name === orgName,
    );
    return org?.roleId === UserRole.Admin;
  }, [userOrganisations, orgName]);

  const navItems = useMemo(
    () => [
      {
        href: registryRoutes.MEMBERS,
        label: navigationMessages['navigation.menuEntry.members.label'],
        selected: pathname.startsWith(registryRoutes.MEMBERS),
        startIcon: <LeadingIcon />,
      },
      {
        href: registryRoutes.ANIMALS,
        label: navigationMessages['navigation.menuEntry.animals.label'],
        selected: pathname.startsWith(registryRoutes.ANIMALS),
        startIcon: <AnimalsIcon />,
      },
      {
        href: registryRoutes.IMPORTS,
        label: navigationMessages['navigation.menuEntry.imports.label'],
        selected: pathname.startsWith(registryRoutes.IMPORTS),
        startIcon: <UploadFileIcon />,
      },
      ...(canManageUserAssignments
        ? [
            {
              href: registryRoutes.USER_ASSIGNMENTS,
              label:
                navigationMessages[
                  'navigation.menuEntry.userAssignments.label'
                ],
              selected: pathname.startsWith(registryRoutes.USER_ASSIGNMENTS),
              startIcon: <GroupIcon />,
            },
          ]
        : []),
      {
        expanded: configMenuOpen,
        href: '',
        label: navigationMessages['navigation.menuEntry.configurations.label'],
        selected:
          pathname.startsWith(registryRoutes.FIELDS_CONFIG) ||
          pathname.startsWith(registryRoutes.FORMS_MANANGER) ||
          pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES) ||
          pathname.startsWith(registryRoutes.RULES),
        startIcon: <AdjustmentsVerticalIcon />,
        endIcon: sidebarExpanded ? (
          <Box>{configMenuOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}</Box>
        ) : undefined,
        subnavs: [
          {
            href: registryRoutes.FIELDS_CONFIG,
            label:
              navigationMessages[
                'navigation.menuEntry.form-builder.fields-config.label'
              ],
            selected: pathname.startsWith(registryRoutes.FIELDS_CONFIG),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.FORMS_MANANGER,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.formsManager.label'
              ],
            selected: pathname.startsWith(registryRoutes.FORMS_MANANGER),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.CERTIFICATE_TEMPLATES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.certificateTemplates.label'
              ],
            selected: pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.RULES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.rules.label'
              ],
            selected: pathname.startsWith(registryRoutes.RULES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
        ],
        onClick: (event: SyntheticEvent) => {
          event.preventDefault();
          setConfigMenuOpen(!configMenuOpen);
        },
      },
    ],
    [
      canManageUserAssignments,
      configMenuOpen,
      pathname,
      registryRoutes,
      sidebarExpanded,
      setConfigMenuOpen,
    ],
  );

  return <ShellNavItems items={navItems} />;
}
